import * as THREE from 'three'

import { AIRCRAFT, CARRIER, PATTERN } from '../config/constants.js'
import { DEG, clamp, pad, wrapDeg360 } from '../util/units.js'
import {
  combinerOnScreen,
  combinerRect,
  hudRadiusPx,
  layoutBox,
  roundedRectPath,
} from './combiner.js'

/**
 * Head-up display, drawn on a 2D canvas over the WebGL view.
 *
 * Canvas rather than WebGL because HUD symbology is thin bright strokes and
 * crisp text — precisely what a 2D context is good at and what a 3D renderer
 * has to fight for. The one thing that genuinely needs 3D is the velocity
 * vector and the pitch ladder, and those are handled by projecting world-space
 * directions through the same camera, so they stay conformal: the flight path
 * marker sits on the point the aircraft is actually going to.
 *
 * The approach block (ball, lineup, closure, indexer) only appears with the
 * gear down. Above the pattern it would be noise.
 *
 * WHAT IS ON THE GLASS AND WHAT IS NOT
 * In the cockpit the flight symbology is confined to the COMBINER — a twenty
 * degree circle fixed to the aeroplane, see combiner.js — because that is what
 * a HUD is, and drawing it edge to edge instead quietly removes the one skill
 * it exists to build. Everything else stays where it is:
 *
 *   The AoA indexer is not HUD symbology. It is a separate three-lamp unit on
 *   the canopy bow, up and left of the glass, and putting it inside the HUD
 *   would be inventing an instrument the aeroplane does not have.
 *
 *   The engineering readouts and the ball repeater are trainer aids and say so.
 *   They live at the edges of the screen where a kneeboard and the lens would
 *   be, rather than pretending to be part of the aircraft.
 *
 * Outside the cockpit there is no glass to be inside, so the same symbology is
 * laid out across the screen as the data overlay it then is.
 */

const GREEN = '#3dff9a'
const AMBER = '#ffb02e'
const RED = '#ff4d4d'
const DIM = 'rgba(61,255,154,0.45)'

export class HUD {
  constructor(container) {
    this.canvas = document.createElement('canvas')
    this.canvas.className = 'hud'
    container.appendChild(this.canvas)
    this.ctx = this.canvas.getContext('2d')

    this.dpr = Math.min(window.devicePixelRatio, 2)
    this.enabled = true

    this._v = new THREE.Vector3()
    this._p = new THREE.Vector3()

    this._onResize = () => this.resize()
    window.addEventListener('resize', this._onResize)
    this.resize()
  }

  resize() {
    const w = this.canvas.parentElement.clientWidth || window.innerWidth
    const h = this.canvas.parentElement.clientHeight || window.innerHeight
    this.canvas.width = Math.floor(w * this.dpr)
    this.canvas.height = Math.floor(h * this.dpr)
    this.w = w
    this.h = h
  }

  /**
   * Project a world-space direction from the camera onto the screen.
   * @returns {{x:number,y:number,visible:boolean}}
   */
  _projectDir(camera, dir) {
    this._p.copy(camera.position).addScaledVector(dir, 4000)
    this._v.copy(this._p).project(camera)
    return {
      x: (this._v.x * 0.5 + 0.5) * this.w,
      y: (-this._v.y * 0.5 + 0.5) * this.h,
      visible: this._v.z < 1,
    }
  }

  /**
   * @param {object} ctx
   * @param {import('../rails/RailsFlight.js').RailState} ctx.state
   * @param {THREE.Camera} ctx.camera
   * @param {boolean} ctx.cockpit
   * @param {object|null} ctx.approach
   * @param {object} ctx.pattern
   * @param {object} ctx.systems
   */
  draw(ctx) {
    const g = this.ctx
    g.setTransform(this.dpr, 0, 0, this.dpr, 0, 0)
    g.clearRect(0, 0, this.w, this.h)
    if (!this.enabled) return

    const { state, camera, cockpit, approach, systems } = ctx
    this._glass = null

    // Vector3.project() reads camera.matrixWorldInverse, which is normally
    // only refreshed inside WebGLRenderer.render(). In the cockpit view the
    // camera's transform is assigned directly rather than through lookAt(), so
    // without this the pitch ladder and the flight path marker are drawn from
    // the PREVIOUS frame's attitude — a degree of lag in a hard break, and
    // complete nonsense on the first frame.
    camera.updateMatrixWorld()
    camera.matrixWorldInverse.copy(camera.matrixWorld).invert()

    g.lineCap = 'butt'
    g.lineJoin = 'miter'
    g.font = '600 13px ui-monospace, monospace'
    g.textBaseline = 'middle'

    if (cockpit) {
      this._combiner(g, camera, state)
    } else {
      // No glass to be inside: the same symbology, laid out across the screen
      // as the data overlay it is in an external view.
      this._tapes(g, state, { cx: this.w / 2, cy: this.h / 2, w: this.w, h: this.h })
    }

    // COMPACT: on a device with on-screen controls, most of this canvas is a
    // second copy of something the DOM panels already say, competing for a
    // screen that has a third of the room.
    //
    // The pattern cue is the third statement of the current step — the phase
    // strip, the card and the primary button all carry it — and it was drawn
    // straight across the phase strip. The engineering readouts and the
    // configuration flags are the jet strip, which is laid out to fit and this
    // is not: they ran under the primary button and the CONTROLS handle.
    //
    // What survives is what is NOT duplicated: the combiner, the AoA indexer
    // and the ball. Those are instruments, not captions.
    this._compact = !!ctx.compact
    if (!this._compact) this._readouts(g, state, systems)

    if (state.gearNorm > 0.5) {
      this._indexer(g, state, cockpit)
      if (approach) this._approachBlock(g, approach, state)
    }

    if (!this._compact) this._patternCue(g, ctx.pattern)

  }

  /* ------------------------------------------------------------------ */

  /**
   * Draw the flight symbology onto the combiner glass.
   *
   * The glass is fixed to the AEROPLANE, not to the screen, so its centre is
   * wherever the aircraft's boresight happens to project. Turn your head and
   * the whole HUD slides across the windscreen and off it, which is what a
   * collimated display bolted to an airframe does.
   */
  _combiner(g, camera, state) {
    const bore = this._projectDir(camera, state.forward)
    if (!bore.visible) return

    const rect = combinerRect(hudRadiusPx(camera.fov, this.h))
    // The glass hangs BELOW the boresight — see HUD_BORESIGHT_BIAS. Symbology
    // painted on the plate is laid out about this centre; conformal symbology
    // stays where the world puts it.
    const cx = bore.x
    const cy = bore.y + rect.centreDy
    if (!combinerOnScreen(cx, cy, rect, this.w, this.h)) return
    // Where the glass ended up, so the indexer can be mounted next to it.
    this._glass = { cx, cy, ...rect }

    g.save()
    // Everything from here is trimmed by the edge of the glass. This is what
    // turns a full-screen overlay into a HUD.
    roundedRectPath(g, cx, cy, rect.halfW, rect.halfH, rect.corner)
    g.clip()

    // Conformal symbology stays in SCREEN space. The pitch ladder and the
    // flight path marker have to sit on the real horizon and the real point the
    // aeroplane is going to — that is the whole point of them — so they are
    // projected through the camera exactly as before and merely trimmed.
    const box = layoutBox(rect)
    const scale = clamp(box.h / 380, 0.8, 1)
    // The bars AND THEIR LABELS have to stop inboard of the airspeed and
    // altitude — the label is what actually collides, and it hangs a dozen
    // pixels further out than the bar it belongs to. A rectangular glass is
    // wider than the circle was, so there is room for a wider ladder than the
    // 2.4 degrees the circle allowed. The horizon is drawn wide regardless and
    // runs off both edges, which is correct: the horizon does not stop at the
    // HUD.
    // The heading scale owns the top of the glass, so the ladder is kept out
    // of it. Without this the fifteen degree bar and its labels land straight
    // on the heading tape — real HUDs draw the ladder in a limited vertical
    // window around the boresight for exactly this reason, rather than running
    // it into the other symbology.
    g.save()
    g.beginPath()
    g.rect(cx - rect.halfW, cy - rect.halfH * 0.6, rect.halfW * 2, rect.halfH * 1.6)
    g.clip()
    this._pitchLadder(g, camera, state, 3.2)
    g.restore()
    this._velocityVector(g, camera, state, { cx, cy, ...rect, scale })

    // Fixed symbology is painted ON the glass, so it scales with the glass.
    this._tapes(g, state, { cx, cy, w: box.w, h: box.h, onGlass: true })

    g.restore()

    // The bezel. Faint, because on a real combiner you are looking at the frame
    // of a piece of glass a foot from your face and it is nearly invisible
    // against the outside world — but it is the cue that says the symbology has
    // an edge, which is the thing being taught.
    g.save()
    g.strokeStyle = 'rgba(61,255,154,0.13)'
    g.lineWidth = 1
    roundedRectPath(g, cx, cy, rect.halfW, rect.halfH, rect.corner)
    g.stroke()
    g.restore()
  }

  /**
   * @param {number} halfWidthDeg how wide the bars are drawn, in degrees of
   *        yaw either side of the nose. Narrower on the glass, where a six
   *        degree bar is wider than the combiner is and its labels land on top
   *        of the airspeed.
   */
  _pitchLadder(g, camera, state, halfWidthDeg = 6) {
    const hdg = state.headingDeg * DEG
    const north = -Math.cos(hdg)
    const east = Math.sin(hdg)

    const dirAt = (pitchDeg, yawOffsetDeg) => {
      const h = hdg + yawOffsetDeg * DEG
      const p = pitchDeg * DEG
      return new THREE.Vector3(
        Math.cos(p) * Math.sin(h),
        Math.sin(p),
        -Math.cos(p) * Math.cos(h),
      )
    }

    g.save()
    for (let pitch = -40; pitch <= 40; pitch += 5) {
      // The horizon is always drawn wide and always runs off both edges of the
      // glass, whatever the ladder bars are doing. It carries no label to
      // collide with anything, and a horizon that stopped short of the edge
      // would read as a ladder bar rather than as the horizon.
      const wide = pitch === 0 ? 14 : halfWidthDeg
      const a = this._projectDir(camera, dirAt(pitch, -wide))
      const b = this._projectDir(camera, dirAt(pitch, wide))
      if (!a.visible || !b.visible) continue
      if (
        Math.max(a.y, b.y) < -this.h * 0.4 ||
        Math.min(a.y, b.y) > this.h * 1.4
      ) {
        continue
      }

      g.strokeStyle = pitch === 0 ? GREEN : DIM
      g.lineWidth = pitch === 0 ? 2 : 1.4
      g.setLineDash(pitch < 0 ? [7, 6] : [])

      if (pitch === 0) {
        // Horizon with a gap in the middle for the flight path marker.
        const mx = (a.x + b.x) / 2
        const my = (a.y + b.y) / 2
        g.beginPath()
        g.moveTo(a.x, a.y)
        g.lineTo(mx - 46, my - (my - a.y) * 0.0)
        g.moveTo(mx + 46, my)
        g.lineTo(b.x, b.y)
        g.stroke()
      } else {
        g.beginPath()
        g.moveTo(a.x, a.y)
        g.lineTo(b.x, b.y)
        g.stroke()

        g.setLineDash([])
        g.fillStyle = DIM
        g.textAlign = 'right'
        g.fillText(String(Math.abs(pitch)), a.x - 6, a.y)
        g.textAlign = 'left'
        g.fillText(String(Math.abs(pitch)), b.x + 6, b.y)
      }
    }
    g.setLineDash([])
    g.restore()

    // Suppress unused-variable lint noise while keeping the intent readable.
    void north
    void east
  }

  /**
   * The flight path marker: where the aeroplane is actually going.
   *
   * @param {{cx:number,cy:number,r:number,scale:number}|null} glass when the
   *        symbology is on a combiner, the marker is CAGED to its edge rather
   *        than allowed off it — a real HUD parks the marker against the side
   *        of the glass and leaves it there, because a flight path you cannot
   *        see is worse than one pinned where you last had it.
   */
  _velocityVector(g, camera, state, glass = null) {
    const speed = state.velocity.length()
    if (speed < 4) return

    const dir = state.velocity.clone().normalize()
    const p = this._projectDir(camera, dir)
    if (!p.visible) return

    const k = glass?.scale ?? 1
    const s = (n) => n * k
    let x
    let y
    let caged = false
    if (glass) {
      // Caged to the glass, which is a rectangle — so the limit is per axis.
      // Caging radially, as the circular version did, parked the marker short
      // of the frame everywhere except dead abeam and dead above.
      const m = s(24)
      x = clamp(p.x, glass.cx - glass.halfW + m, glass.cx + glass.halfW - m)
      y = clamp(p.y, glass.cy - glass.halfH + m, glass.cy + glass.halfH - m)
      // AND SAY SO. On speed the Tomcat carries 7.8 degrees of pitch on a 3.5
      // degree flight path, so the flight path marker sits eleven degrees below
      // the nose — off the bottom of a 17.8 degree combiner however it is
      // aimed. That is a real property of the aeroplane and the reason an F-14
      // approach is flown on the indexer and the ball. What the display owes
      // the pilot is not to lie about it: a marker silently parked against the
      // frame still reads as "this is where you are going", which is the one
      // thing it is no longer telling you.
      caged = Math.abs(x - p.x) > 0.5 || Math.abs(y - p.y) > 0.5
    } else {
      x = clamp(p.x, 20, this.w - 20)
      y = clamp(p.y, 20, this.h - 20)
    }

    g.strokeStyle = GREEN
    g.lineWidth = 2
    // Dashed when caged: the same symbol, drawn so you can tell at a glance
    // that it is at a limit rather than at a position.
    g.setLineDash(caged ? [s(4), s(4)] : [])
    g.beginPath()
    g.arc(x, y, s(9), 0, Math.PI * 2)
    g.moveTo(x - s(9), y)
    g.lineTo(x - s(22), y)
    g.moveTo(x + s(9), y)
    g.lineTo(x + s(22), y)
    g.moveTo(x, y - s(9))
    g.lineTo(x, y - s(18))
    g.stroke()
    g.setLineDash([])

    // On-speed AoA bracket. When the flight path marker sits inside the
    // bracket you are on speed — the same cue as the indexer, without having
    // to look away from the deck.
    if (state.gearNorm > 0.5) {
      const err = state.aoaUnits - AIRCRAFT.ONSPEED_AOA_UNITS
      const inBand = Math.abs(err) <= AIRCRAFT.AOA_DONUT_BAND
      g.strokeStyle = inBand ? GREEN : Math.abs(err) < AIRCRAFT.AOA_CHEVRON_BAND ? AMBER : RED
      g.lineWidth = 2
      const off = clamp(err * s(5), s(-34), s(34))
      g.beginPath()
      g.moveTo(x - s(30), y - s(14) + off)
      g.lineTo(x - s(30), y + s(14) + off)
      g.moveTo(x - s(30), y - s(14) + off)
      g.lineTo(x - s(22), y - s(14) + off)
      g.moveTo(x - s(30), y + s(14) + off)
      g.lineTo(x - s(22), y + s(14) + off)
      g.stroke()
    }
  }

  /**
   * Airspeed, altitude and heading, laid out inside a box.
   *
   * The box is the whole screen in an external view and the combiner glass in
   * the cockpit — a twenty degree circle roughly a quarter the height of the
   * window. Everything is therefore expressed as a fraction of the box rather
   * than of the screen, and the type scales with it, or the labels come out
   * bigger than the instrument they belong to.
   */
  _tapes(g, state, box) {
    const { cx, cy, w, h } = box
    const tapeH = Math.min(h * 0.46, 340)
    // Type has to shrink with the glass, but not as far as it first did.
    //
    // HUD characters are about a thirtieth of the combiner's diameter — that is
    // what makes them legible at a glance while your attention is out at the
    // ramp — so on a 400 px circle they want to be around 13 px, not 8. Clamped
    // at the bottom for legibility and at the top so the external view does not
    // get billboard lettering.
    const scale = clamp(h / 380, 0.8, 1)
    const px = (n) => `600 ${(n * scale).toFixed(1)}px ui-monospace, monospace`
    g.font = px(13)

    // ---- airspeed, left --------------------------------------------------
    this._tape(g, {
      x: cx - w * 0.28,
      y: cy,
      h: tapeH,
      value: state.kcas,
      step: 10,
      majorStep: 50,
      perUnit: tapeH / 160,
      align: 'right',
      label: box.onGlass ? null : 'KIAS',
      scale,
    })

    // ---- altitude, right -------------------------------------------------
    this._tape(g, {
      x: cx + w * 0.28,
      y: cy,
      h: tapeH,
      value: state.altFt,
      step: 100,
      majorStep: 500,
      // 1,600 ft across the tape, not 2,400. On a combiner a quarter the
      // height of the window, 2,400 ft put the five-hundred-foot labels
      // twenty-two pixels apart and they ran into each other. Eight hundred
      // feet either side is more than a CASE 1 pattern ever needs.
      perUnit: tapeH / 1600,
      align: 'left',
      label: box.onGlass ? null : 'FT MSL',
      scale,
    })

    // ---- heading, top ----------------------------------------------------
    const hdg = wrapDeg360(state.headingDeg)
    // Eighteen degrees either side, not sixty.
    //
    // Sixty was survivable across a whole window and unreadable on the glass:
    // twenty-five ticks and a dozen two-digit labels crammed into ninety pixels,
    // which rendered as a single grey smear. A real HUD heading scale shows
    // fifteen or twenty degrees either side of the nose, for exactly this
    // reason — the glass is small and the tape has to be legible at a glance.
    const spanDeg = 18
    const tapeW = w * 0.52
    const pxPerDeg = tapeW / (spanDeg * 2)
    // Relative to the box, not pinned near the top of the window: on the glass
    // the heading tape sits just under the upper edge, and an absolute 46 px
    // floor put it outside a combiner only 250 px across.
    const topY = cy - h * 0.40

    g.save()
    g.beginPath()
    g.rect(cx - tapeW / 2, topY - 22 * scale, tapeW, 34 * scale)
    g.clip()
    g.strokeStyle = DIM
    g.fillStyle = GREEN
    g.textAlign = 'center'
    for (let d = -spanDeg; d <= spanDeg; d += 5) {
      const tick = Math.round((hdg + d) / 5) * 5
      const x = cx + (tick - hdg) * pxPerDeg
      const major = tick % 10 === 0
      g.lineWidth = major ? 1.6 : 1
      g.beginPath()
      g.moveTo(x, topY - 6 * scale)
      g.lineTo(x, topY + (major ? 6 : 2) * scale)
      g.stroke()
      if (major) g.fillText(pad(wrapDeg360(tick) / 10, 2), x, topY - 15 * scale)
    }
    g.restore()

    g.strokeStyle = GREEN
    g.lineWidth = 2
    g.beginPath()
    g.moveTo(cx, topY + 12 * scale)
    g.lineTo(cx - 6 * scale, topY + 21 * scale)
    g.lineTo(cx + 6 * scale, topY + 21 * scale)
    g.closePath()
    g.stroke()
    g.fillStyle = GREEN
    g.textAlign = 'center'
    g.fillText(pad(hdg, 3), cx, topY + 34 * scale)
    g.font = '600 13px ui-monospace, monospace'
  }

  _tape(g, o) {
    const { x, y, h, value, step, majorStep, perUnit, align, label } = o
    const half = h / 2
    // Every fixed dimension in here is in "screen pixels at full size", so one
    // multiplier carries the whole tape onto a combiner a quarter the height.
    const k = o.scale ?? 1
    const s = (n) => n * k

    g.save()
    g.beginPath()
    g.rect(align === 'right' ? x - s(74) : x - s(6), y - half, s(80), h)
    g.clip()

    g.strokeStyle = DIM
    g.fillStyle = GREEN
    g.textAlign = align
    const first = Math.floor((value - half / perUnit) / step) * step
    const last = Math.ceil((value + half / perUnit) / step) * step
    for (let v = first; v <= last; v += step) {
      const ty = y + (value - v) * perUnit
      const major = Math.abs(v % majorStep) < 1e-6
      g.lineWidth = major ? 1.8 : 1
      const len = s(major ? 14 : 7)
      g.beginPath()
      if (align === 'right') {
        g.moveTo(x, ty)
        g.lineTo(x - len, ty)
      } else {
        g.moveTo(x, ty)
        g.lineTo(x + len, ty)
      }
      g.stroke()
      // The readout window blanks the scale behind it, the way a real tape
      // does. Without this the value box lands on top of two or three tick
      // labels — invisible on a full-screen layout where the ticks are a
      // hundred pixels apart, and unreadable on a combiner where they are
      // thirty.
      const behindBox = Math.abs(ty - y) < s(16)
      // And a label whose centre is outside the window is not drawn at all,
      // rather than drawn and sliced in half by the clip. A tape ending in a
      // severed numeral reads as a rendering fault, not as a scale running off
      // the end of its window.
      const outsideWindow = Math.abs(ty - y) > half - s(7)
      if (major && !behindBox && !outsideWindow) {
        g.fillText(String(Math.round(v)), align === 'right' ? x - s(20) : x + s(20), ty)
      }
    }
    g.restore()

    // Current value box.
    g.strokeStyle = GREEN
    g.fillStyle = 'rgba(4,10,12,0.88)'
    g.lineWidth = 1.6
    // 52, not 66. On the glass the tape sits about sixty pixels from centre, so
    // a sixty-six pixel readout window reaches all the way in to the flight
    // path marker. Four digits of altitude still fit.
    const bw = s(52)
    const bx = align === 'right' ? x - bw - s(2) : x + s(2)
    g.beginPath()
    g.rect(bx, y - s(13), bw, s(26))
    g.fill()
    g.stroke()
    g.fillStyle = GREEN
    g.textAlign = 'center'
    // 14, not 16. HUD characters are all one size — that is what makes the
    // display readable at a glance — and on a combiner a quarter the height of
    // the window a 16 px numeral is a twentieth of the whole instrument.
    g.font = `700 ${s(14).toFixed(1)}px ui-monospace, monospace`
    g.fillText(String(Math.round(value)), bx + bw / 2, y)
    // Unit labels only in the external view. On the glass there is no room —
    // they land in the heading tape — and no need: a real HUD does not caption
    // its airspeed "KIAS", because the pilot knows what the left number is.
    if (label) {
      g.font = `600 ${s(11).toFixed(1)}px ui-monospace, monospace`
      g.fillStyle = DIM
      g.fillText(label, bx + bw / 2, y - half - s(14))
    }
    g.font = `600 ${s(13).toFixed(1)}px ui-monospace, monospace`
  }

  _readouts(g, state, systems) {
    const x = 22
    let y = this.h - 150
    g.textAlign = 'left'
    g.fillStyle = GREEN
    g.font = '600 12px ui-monospace, monospace'

    const line = (text, color = GREEN) => {
      g.fillStyle = color
      g.fillText(text, x, y)
      y += 17
    }

    line(`M ${state.mach.toFixed(2)}`)
    line(`G ${state.nz.toFixed(1)}`)
    line(`AOA ${state.aoaUnits.toFixed(1)}`)
    line(`VS ${Math.round(state.vsFpm)}`)
    line(`GS ${Math.round(state.groundspeedKts)}`)
    line(`FUEL ${Math.round(state.fuelLbs)}`)
    line(`GW ${Math.round(state.weightLbs)}`)

    // Configuration strip, bottom right.
    const rx = this.w - 22
    let ry = this.h - 150
    g.textAlign = 'right'
    const flag = (text, on, color = GREEN) => {
      g.fillStyle = on ? color : 'rgba(61,255,154,0.22)'
      g.fillText(text, rx, ry)
      ry += 17
    }
    flag('GEAR', state.gearNorm > 0.98)
    flag('FLAPS', state.flapNorm > 0.9)
    flag('HOOK', state.hookNorm > 0.9, AMBER)
    flag('SPD BRK', state.speedbrakeNorm > 0.05, AMBER)
    flag('DLC', state.dlcNorm > 0.05, AMBER)
    flag('APC', !!systems?.apcEngaged)
    flag(`SWEEP ${Math.round(state.sweepDeg)}`, true, systems?.sweepManual ? AMBER : GREEN)
    flag('A/B', state.augmenting, RED)
  }

  /**
   * The three indexer lamps.
   *
   * NOT HUD symbology, and deliberately not drawn on the glass. On an F-14 the
   * approach indexer is a separate three-lamp unit mounted on the canopy bow up
   * and to the LEFT of the combiner, exactly so that it is in peripheral vision
   * while you are looking through the HUD at the ball. Moving it inside the
   * twenty degree circle would be inventing an instrument the aeroplane does
   * not have, and crowding the one it does.
   */
  _indexer(g, state, cockpit = false) {
    // Mounted RELATIVE TO THE GLASS, not at a fraction of the window.
    //
    // A fraction of the window leaves it stranded halfway across the sky the
    // moment the combiner is a small circle rather than the whole screen — and
    // it stops tracking when the pilot looks around, so the lamps sit still
    // while the HUD they belong beside slides away. The real unit is bolted to
    // the canopy bow a few inches up and left of the combiner, and it goes
    // where the combiner goes.
    const glass = cockpit ? this._glass : null
    // In the external view it sits outboard of the airspeed tape, which moved
    // outboard itself when the layout was rebalanced for the glass — at the old
    // 0.30 the lamps ended up drawn straight through the airspeed readout.
    const x = glass ? glass.cx - glass.halfW * 1.2 : this.w * 0.13
    const y = glass ? glass.cy - glass.halfH * 0.78 : this.h / 2
    // The lamps scale with the glass for the same reason the symbology does.
    const k = glass ? clamp(glass.halfH / 150, 0.72, 1.25) : 1
    const u = (n) => n * k
    const err = state.aoaUnits - AIRCRAFT.ONSPEED_AOA_UNITS
    const onSpeed = Math.abs(err) <= AIRCRAFT.AOA_DONUT_BAND

    // Slow: chevron up, add power. Fast: chevron down.
    g.lineWidth = 2.4 * k
    g.strokeStyle = err > AIRCRAFT.AOA_DONUT_BAND * 0.6 ? '#22ff55' : 'rgba(34,255,85,0.15)'
    g.beginPath()
    g.moveTo(x - u(9), y - u(20))
    g.lineTo(x, y - u(30))
    g.lineTo(x + u(9), y - u(20))
    g.stroke()

    g.strokeStyle = onSpeed ? AMBER : 'rgba(255,176,46,0.15)'
    g.beginPath()
    g.arc(x, y, u(9), 0, Math.PI * 2)
    g.stroke()

    g.strokeStyle = err < -AIRCRAFT.AOA_DONUT_BAND * 0.6 ? RED : 'rgba(255,59,48,0.15)'
    g.beginPath()
    g.moveTo(x - u(9), y + u(20))
    g.lineTo(x, y + u(30))
    g.lineTo(x + u(9), y + u(20))
    g.stroke()
  }

  /**
   * Ball repeater, lineup scale and closure. Not a real F-14 instrument — a
   * training aid, and labelled as one, because reading the actual lens at the
   * resolution of a browser window is genuinely harder than it is from a
   * cockpit.
   */
  _approachBlock(g, a, state) {
    if (a.rangeToRampFt > 6000 || a.rangeToRampFt < -60) return

    const w = this._compact ? 108 : 130
    const h = this._compact ? 124 : 150
    // Bottom LEFT when there are on-screen controls. Its usual home on the
    // right is where the jet strip lives on a phone, and the two were drawn on
    // top of each other; the left side below the checklist is the one part of a
    // handset in landscape that nothing else wants.
    const x = this._compact ? 14 : this.w - w - 24
    const y = this._compact ? this.h - h - 74 : this.h / 2 - h / 2


    g.save()
    g.fillStyle = 'rgba(4,10,12,0.55)'
    g.strokeStyle = 'rgba(61,255,154,0.35)'
    g.lineWidth = 1
    g.beginPath()
    g.rect(x, y, w, h)
    g.fill()
    g.stroke()

    const cx = x + w / 2
    const cy = y + 62

    // Datum lights.
    g.strokeStyle = GREEN
    g.lineWidth = 3
    g.beginPath()
    g.moveTo(cx - 44, cy)
    g.lineTo(cx - 16, cy)
    g.moveTo(cx + 16, cy)
    g.lineTo(cx + 44, cy)
    g.stroke()

    // The ball.
    const ballY = cy - clamp(a.ballNorm, -1.2, 1.2) * 44
    const pegged = Math.abs(a.ballNorm) > 0.95
    g.fillStyle = pegged && (performance.now() % 500 < 250) ? 'rgba(255,176,46,0.25)' : AMBER
    g.beginPath()
    g.arc(cx, ballY, 8, 0, Math.PI * 2)
    g.fill()

    // Lineup scale.
    const ly = y + h - 46
    g.strokeStyle = 'rgba(61,255,154,0.5)'
    g.lineWidth = 1
    g.beginPath()
    g.moveTo(cx - 48, ly)
    g.lineTo(cx + 48, ly)
    g.moveTo(cx, ly - 6)
    g.lineTo(cx, ly + 6)
    g.stroke()

    const lineupPx = clamp(a.lineupFt * 2.6, -48, 48)
    g.fillStyle = Math.abs(a.lineupFt) < 8 ? GREEN : Math.abs(a.lineupFt) < 20 ? AMBER : RED
    g.beginPath()
    g.moveTo(cx + lineupPx, ly - 9)
    g.lineTo(cx + lineupPx - 5, ly - 17)
    g.lineTo(cx + lineupPx + 5, ly - 17)
    g.closePath()
    g.fill()

    // Numbers.
    g.font = '600 10px ui-monospace, monospace'
    g.textAlign = 'center'
    g.fillStyle = DIM
    g.fillText('BALL', cx, y + 12)
    g.fillText(
      `${a.rangeToRampFt > 0 ? Math.round(a.rangeToRampFt) : 0} FT TO RAMP`,
      cx,
      y + h - 22,
    )
    g.fillStyle = Math.abs(a.lineupFt) < 8 ? GREEN : AMBER
    g.fillText(
      `LU ${a.lineupFt >= 0 ? 'R' : 'L'}${Math.abs(a.lineupFt).toFixed(0)}`,
      cx,
      y + h - 9,
    )
    g.restore()

    // Hook-to-ramp clearance in the last few hundred feet: the number that
    // decides between a 3 wire and a ramp strike.
    if (a.rangeToRampFt > 0 && a.rangeToRampFt < 900) {
      const clearance = a.deck.z - AIRCRAFT.HOOK_TO_EYE_FT
      g.textAlign = 'center'
      g.font = '700 15px ui-monospace, monospace'
      g.fillStyle = clearance < 12 ? RED : clearance < 20 ? AMBER : GREEN
      g.fillText(`HOOK ${Math.round(clearance)} FT`, this.w / 2, this.h - 44)
    }
    g.font = '600 13px ui-monospace, monospace'
    void state
  }

  _patternCue(g, pattern) {
    if (!pattern?.phaseLabel) return
    g.textAlign = 'center'
    g.font = '700 15px ui-monospace, monospace'
    g.fillStyle = AMBER
    g.fillText(pattern.phaseLabel, this.w / 2, Math.max(26, this.h * 0.055))

    if (pattern.cue) {
      g.font = '600 13px ui-monospace, monospace'
      g.fillStyle = pattern.cueUrgent ? RED : '#ffe6a8'
      g.fillText(pattern.cue, this.w / 2, Math.max(46, this.h * 0.055 + 20))
    }
    g.font = '600 13px ui-monospace, monospace'
  }

  dispose() {
    window.removeEventListener('resize', this._onResize)
    this.canvas.remove()
  }
}

/** Re-exported so callers do not have to import constants just to tune the HUD. */
export const HUD_CONSTANTS = { GREEN, AMBER, RED, PATTERN, CARRIER }
